import tw from '@/common/utils/twmacro';
import { fonts } from '@/dashboard/LessonPlan/utils';
import {
  ArrowUUpLeftIcon,
  ArrowUUpRightIcon,
  CaretDownIcon,
  ChalkboardTeacherIcon,
  DotsThreeVerticalIcon,
  FileTextIcon,
  HighlighterCircleIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  MinusIcon,
  PlusIcon,
  PrinterIcon,
  RulerIcon,
  TextAUnderlineIcon,
  TextAlignCenterIcon,
  TextAlignJustifyIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
  TextBIcon,
  TextItalicIcon,
  TextStrikethroughIcon,
  TextUnderlineIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shadcn/components/popover';
import { Separator } from '@shadcn/components/separator';
import { cn } from '@shadcn/lib/utils';
import { Editor } from '@tiptap/react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Google Docs-style font color presets (10 columns × 6 rows)
const FONT_COLOR_PRESETS = [
  // Row 1 — dark
  '#000000',
  '#434343',
  '#666666',
  '#999999',
  '#b7b7b7',
  '#cccccc',
  '#d9d9d9',
  '#efefef',
  '#f3f3f3',
  '#ffffff',
  // Row 2 — vivid
  '#980000',
  '#ff0000',
  '#ff9900',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#4a86e8',
  '#0000ff',
  '#9900ff',
  '#ff00ff',
  // Row 3
  '#e6b8af',
  '#f4cccc',
  '#fce5cd',
  '#fff2cc',
  '#d9ead3',
  '#d0e0e3',
  '#c9daf8',
  '#cfe2f3',
  '#d9d2e9',
  '#ead1dc',
  // Row 4
  '#dd7e6b',
  '#ea9999',
  '#f9cb9c',
  '#ffe599',
  '#b6d7a8',
  '#a2c4c9',
  '#a4c2f4',
  '#9fc5e8',
  '#b4a7d6',
  '#d5a6bd',
  // Row 5
  '#cc4125',
  '#e06666',
  '#f6b26b',
  '#ffd966',
  '#93c47d',
  '#76a5af',
  '#6d9eeb',
  '#6fa8dc',
  '#8e7cc3',
  '#c27ba0',
  // Row 6
  '#a61c00',
  '#cc0000',
  '#e69138',
  '#f1c232',
  '#6aa84f',
  '#45818e',
  '#3c78d8',
  '#3d85c6',
  '#674ea7',
  '#a64d79',
];

// Highlight-friendly pastel presets (5 columns × 4 rows)
const HIGHLIGHT_PRESETS = [
  '#fef08a',
  '#fde68a',
  '#fed7aa',
  '#fecaca',
  '#fbcfe8',
  '#e9d5ff',
  '#c7d2fe',
  '#bfdbfe',
  '#a7f3d0',
  '#bbf7d0',
  '#d9f99d',
  '#fef9c3',
  '#fce7f3',
  '#dbeafe',
  '#f3e8ff',
  '#ccfbf1',
  '#cffafe',
  '#e0e7ff',
  '#ffe4e6',
  '#fef3c7',
];

const BULLET_STYLES = [
  { label: '• Disc', value: 'disc' },
  { label: '○ Circle', value: 'circle' },
  { label: '■ Square', value: 'square' },
] as const;

const ORDERED_STYLES = [
  { label: '1. Decimal', value: null },
  { label: 'a. Lower Alpha', value: 'a' },
  { label: 'i. Lower Roman', value: 'i' },
  { label: 'A. Upper Alpha', value: 'A' },
  { label: 'I. Upper Roman', value: 'I' },
] as const;

const HEADING_OPTIONS = [
  { label: 'Normal text', level: 0 },
  { label: 'Heading 1', level: 1 },
  { label: 'Heading 2', level: 2 },
  { label: 'Heading 3', level: 3 },
] as const;

interface FixedToolbarProps {
  editor: Editor | null;
  onExportToPdf: () => void;
  onCreateActivitySheet?: () => void;
  onExportToPpt?: () => void;
}
function getCurrentFontLabel(editor: Editor): string {
  const family = editor.getAttributes('textStyle').fontFamily || 'inherit';
  const match = fonts.find((f) => f.value === family);
  return match?.name ?? 'Helvetica';
}

export default function FixedToolbar({
  editor,
  onExportToPdf,
  onCreateActivitySheet,
  onExportToPpt,
}: FixedToolbarProps) {
  const [currentFontSize, setCurrentFontSize] = useState('');
  const [fontOpen, setFontOpen] = useState(false);
  const fontRef = useRef<HTMLDivElement>(null);

  const [headingOpen, setHeadingOpen] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);

  const [fontColorOpen, setFontColorOpen] = useState(false);
  const fontColorRef = useRef<HTMLDivElement>(null);

  const [highlightOpen, setHighlightOpen] = useState(false);
  const highlightRef = useRef<HTMLDivElement>(null);

  const [bulletOpen, setBulletOpen] = useState(false);
  const bulletRef = useRef<HTMLDivElement>(null);

  const [orderedOpen, setOrderedOpen] = useState(false);
  const orderedRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const openStates = [
      {
        open: headingOpen,
        ref: headingRef,
        close: () => setHeadingOpen(false),
      },
      { open: fontOpen, ref: fontRef, close: () => setFontOpen(false) },
      {
        open: fontColorOpen,
        ref: fontColorRef,
        close: () => setFontColorOpen(false),
      },
      {
        open: highlightOpen,
        ref: highlightRef,
        close: () => setHighlightOpen(false),
      },
      { open: bulletOpen, ref: bulletRef, close: () => setBulletOpen(false) },
      {
        open: orderedOpen,
        ref: orderedRef,
        close: () => setOrderedOpen(false),
      },
    ];
    const activeDropdowns = openStates.filter((s) => s.open);
    if (activeDropdowns.length === 0) return;

    function handleClick(e: MouseEvent) {
      for (const dropdown of activeDropdowns) {
        if (
          dropdown.ref.current &&
          !dropdown.ref.current.contains(e.target as Node)
        ) {
          dropdown.close();
        }
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [
    headingOpen,
    fontOpen,
    fontColorOpen,
    highlightOpen,
    bulletOpen,
    orderedOpen,
  ]);

  useEffect(() => {
    if (!editor) return;

    const updateFontSize = () => {
      const attrs = editor.getAttributes('textStyle');
      if (attrs.fontSize) {
        const size = attrs.fontSize.replace('px', '');
        setCurrentFontSize(size);
      } else {
        setCurrentFontSize('16');
      }
    };

    updateFontSize();

    editor.on('selectionUpdate', updateFontSize);
    editor.on('transaction', updateFontSize);

    return () => {
      editor.off('selectionUpdate', updateFontSize);
      editor.off('transaction', updateFontSize);
    };
  }, [editor]);

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentFontSize(value);

    if (!isNaN(parseInt(value)) && parseInt(value) > 0) {
      editor?.chain().focus().setFontSize(`${value}px`).run();
    }
  };

  const handleFontSizeBlur = () => {
    if (!editor) return;

    const size = parseInt(currentFontSize);
    if (isNaN(size) || size < 1) {
      setCurrentFontSize('16');
      editor.chain().focus().setFontSize('16px').run();
    }
  };

  const handleDecreaseFontSize = () => {
    if (!editor) return;
    let currentSize = parseInt(currentFontSize) || 16;
    let newSize = Math.max(currentSize - 1, 1);
    setCurrentFontSize(newSize.toString());
    editor.chain().focus().setFontSize(`${newSize}px`).run();
  };

  const handleIncreaseFontSize = () => {
    if (!editor) return;
    let currentSize = parseInt(currentFontSize) || 16;
    let newSize = currentSize + 1;
    setCurrentFontSize(newSize.toString());
    editor.chain().focus().setFontSize(`${newSize}px`).run();
  };

  if (!editor) {
    return null;
  }

  const currentFontColor = editor.getAttributes('textStyle').color || '#000000';
  const currentHighlight = editor.getAttributes('highlight').color || null;

  return (
    <ToolbarContainer>
      {/* History */}
      <ToolbarGroup>
        <ToolbarButton
          title="Undo"
          icon={<ArrowUUpLeftIcon size={20} />}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolbarButton
          title="Redo"
          icon={<ArrowUUpRightIcon size={20} />}
          onClick={() => editor.chain().focus().redo().run()}
        />
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Print / Export */}
      <ToolbarGroup>
        <ToolbarButton
          title="Print / Export to PDF"
          icon={<PrinterIcon size={20} />}
          onClick={onExportToPdf}
        />
        {onCreateActivitySheet && (
          <ToolbarButton
            title="Export to Student Worksheet"
            icon={<FileTextIcon size={20} />}
            onClick={onCreateActivitySheet}
          />
        )}
        {onExportToPpt && (
          <ToolbarButton
            title="Export to PowerPoint"
            icon={<ChalkboardTeacherIcon size={20} />}
            onClick={onExportToPpt}
          />
        )}
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Headings */}
      <ToolbarGroup>
        <HeadingWrapper ref={headingRef}>
          <HeadingTrigger
            onClick={() => setHeadingOpen((v) => !v)}
            title="Text style"
          >
            <HeadingLabel>
              {editor.isActive('heading', { level: 1 })
                ? 'Heading 1'
                : editor.isActive('heading', { level: 2 })
                  ? 'Heading 2'
                  : editor.isActive('heading', { level: 3 })
                    ? 'Heading 3'
                    : 'Normal text'}
            </HeadingLabel>
            <CaretDownIcon size={14} />
          </HeadingTrigger>
          {headingOpen && (
            <HeadingDropdown>
              {HEADING_OPTIONS.map((opt) => {
                const isActive =
                  opt.level === 0
                    ? !editor.isActive('heading')
                    : editor.isActive('heading', { level: opt.level });
                return (
                  <HeadingItem
                    key={opt.level}
                    $active={isActive}
                    $level={opt.level}
                    onClick={() => {
                      if (opt.level === 0) {
                        // If currently in a heading, toggle it off to return to paragraph
                        if (editor.isActive('heading', { level: 1 }))
                          editor
                            .chain()
                            .focus()
                            .toggleHeading({ level: 1 })
                            .run();
                        else if (editor.isActive('heading', { level: 2 }))
                          editor
                            .chain()
                            .focus()
                            .toggleHeading({ level: 2 })
                            .run();
                        else if (editor.isActive('heading', { level: 3 }))
                          editor
                            .chain()
                            .focus()
                            .toggleHeading({ level: 3 })
                            .run();
                      } else {
                        editor
                          .chain()
                          .focus()
                          .toggleHeading({ level: opt.level as 1 | 2 | 3 })
                          .run();
                      }
                      setHeadingOpen(false);
                    }}
                  >
                    {opt.label}
                  </HeadingItem>
                );
              })}
            </HeadingDropdown>
          )}
        </HeadingWrapper>
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Font */}
      <ToolbarGroup>
        <FontWrapper ref={fontRef}>
          <FontTrigger onClick={() => setFontOpen((v) => !v)} title="Font">
            <FontLabel>{getCurrentFontLabel(editor)}</FontLabel>
            <CaretDownIcon size={14} />
          </FontTrigger>
          {fontOpen && (
            <FontDropdown>
              {fonts.map((font) => {
                const isActive =
                  (editor.getAttributes('textStyle').fontFamily ||
                    'inherit') === font.value;
                return (
                  <FontItem
                    key={font.value}
                    $active={isActive}
                    style={{
                      fontFamily:
                        font.value === 'inherit' ? 'inherit' : font.value,
                    }}
                    onClick={() => {
                      if (font.value === 'inherit') {
                        editor.chain().focus().unsetFontFamily().run();
                      } else {
                        editor.chain().focus().setFontFamily(font.value).run();
                      }
                      setFontOpen(false);
                    }}
                  >
                    {font.name}
                  </FontItem>
                );
              })}
            </FontDropdown>
          )}
        </FontWrapper>
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Font Size */}
      <ToolbarGroup className="gap-1">
        <ToolbarButton
          onClick={handleDecreaseFontSize}
          title="Decrease Font Size"
          icon={<MinusIcon size={16} />}
        />
        <FontSizeInput
          className="w-11"
          type="number"
          value={currentFontSize}
          onChange={handleFontSizeChange}
          onBlur={handleFontSizeBlur}
          min={1}
          step={1}
          title="Font Size"
        />
        <ToolbarButton
          onClick={handleIncreaseFontSize}
          title="Increase Font Size"
          icon={<PlusIcon size={16} />}
        />
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Text Formatting */}
      <ToolbarGroup>
        <ToolbarButton
          title="Bold"
          icon={<TextBIcon size={20} />}
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
        />
        <ToolbarButton
          title="Italic"
          icon={<TextItalicIcon size={20} />}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
        />
        <ToolbarButton
          title="Underline"
          icon={<TextUnderlineIcon size={20} />}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
        />
        <ToolbarButton
          title="Strikethrough"
          icon={<TextStrikethroughIcon size={20} />}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
        />
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Font Color */}
      <ToolbarGroup>
        <ColorDropdownWrapper ref={fontColorRef}>
          <Button
            variant="ghost"
            size="icon"
            title="Font Color"
            onClick={() => setFontColorOpen((v) => !v)}
          >
            <ColorIconWrapper>
              <TextAUnderlineIcon size={20} />
              <ColorBar style={{ backgroundColor: currentFontColor }} />
            </ColorIconWrapper>
          </Button>
          {fontColorOpen && (
            <ColorGridDropdown>
              <ColorGrid $columns={10}>
                {FONT_COLOR_PRESETS.map((color) => (
                  <ColorSwatch
                    key={color}
                    $color={color}
                    $active={currentFontColor === color}
                    title={color}
                    onClick={() => {
                      editor.chain().focus().setColor(color).run();
                      setFontColorOpen(false);
                    }}
                  />
                ))}
              </ColorGrid>
              <ResetButton
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setFontColorOpen(false);
                }}
              >
                Reset
              </ResetButton>
            </ColorGridDropdown>
          )}
        </ColorDropdownWrapper>

        {/* Highlight */}
        <ColorDropdownWrapper ref={highlightRef}>
          <Button
            variant="ghost"
            size="icon"
            title="Highlight"
            className={cn(currentHighlight ? 'bg-neutral-200/80' : '')}
            onClick={() => setHighlightOpen((v) => !v)}
          >
            <ColorIconWrapper>
              <HighlighterCircleIcon size={20} />
              <ColorBar
                style={{
                  backgroundColor: currentHighlight || '#fef08a',
                }}
              />
            </ColorIconWrapper>
          </Button>
          {highlightOpen && (
            <ColorGridDropdown>
              <ColorGrid $columns={5}>
                {HIGHLIGHT_PRESETS.map((color) => (
                  <ColorSwatch
                    key={color}
                    $color={color}
                    $active={currentHighlight === color}
                    title={color}
                    onClick={() => {
                      editor.chain().focus().toggleHighlight({ color }).run();
                      setHighlightOpen(false);
                    }}
                  />
                ))}
              </ColorGrid>
              <ResetButton
                onClick={() => {
                  editor.chain().focus().unsetHighlight().run();
                  setHighlightOpen(false);
                }}
              >
                Remove
              </ResetButton>
            </ColorGridDropdown>
          )}
        </ColorDropdownWrapper>
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Alignment */}
      <ToolbarGroup>
        <ToolbarButton
          title="Align Left"
          icon={<TextAlignLeftIcon size={20} />}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
        />
        <ToolbarButton
          title="Align Center"
          icon={<TextAlignCenterIcon size={20} />}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
        />
        <ToolbarButton
          title="Align Right"
          icon={<TextAlignRightIcon size={20} />}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
        />
        <ToolbarButton
          title="Justify"
          icon={<TextAlignJustifyIcon size={20} />}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
        />
        <Separator orientation="vertical" className="mx-2" />
      </ToolbarGroup>

      {/* Bullet List */}
      <ToolbarGroup>
        <ListDropdownWrapper ref={bulletRef}>
          <Button
            variant="ghost"
            size="icon"
            title="Bullet List"
            className={cn(
              editor.isActive('bulletList') ? 'bg-neutral-200/80' : '',
            )}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <ListBulletsIcon size={20} />
          </Button>
          <CaretButton
            title="Bullet style"
            onClick={() => setBulletOpen((v) => !v)}
          >
            <CaretDownIcon size={12} />
          </CaretButton>
          {bulletOpen && (
            <ListStyleDropdown>
              {BULLET_STYLES.map((style) => {
                const isActive =
                  editor.isActive('bulletList') &&
                  (editor.getAttributes('bulletList').listStyleType ||
                    'disc') === style.value;
                return (
                  <ListStyleItem
                    key={style.value}
                    $active={isActive}
                    onClick={() => {
                      if (!editor.isActive('bulletList')) {
                        editor.chain().focus().toggleBulletList().run();
                      }
                      editor
                        .chain()
                        .focus()
                        .updateAttributes('bulletList', {
                          listStyleType: style.value,
                        })
                        .run();
                      setBulletOpen(false);
                    }}
                  >
                    {style.label}
                  </ListStyleItem>
                );
              })}
            </ListStyleDropdown>
          )}
        </ListDropdownWrapper>

        {/* Ordered List */}
        <ListDropdownWrapper ref={orderedRef}>
          <Button
            variant="ghost"
            size="icon"
            title="Numbered List"
            className={cn(
              editor.isActive('orderedList') ? 'bg-neutral-200/80' : '',
            )}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListNumbersIcon size={20} />
          </Button>
          <CaretButton
            title="Numbering style"
            onClick={() => setOrderedOpen((v) => !v)}
          >
            <CaretDownIcon size={12} />
          </CaretButton>
          {orderedOpen && (
            <ListStyleDropdown>
              {ORDERED_STYLES.map((style) => {
                const isActive =
                  editor.isActive('orderedList') &&
                  (editor.getAttributes('orderedList').type || null) ===
                    style.value;
                return (
                  <ListStyleItem
                    key={style.label}
                    $active={isActive}
                    onClick={() => {
                      if (!editor.isActive('orderedList')) {
                        editor.chain().focus().toggleOrderedList().run();
                      }
                      editor
                        .chain()
                        .focus()
                        .updateAttributes('orderedList', {
                          type: style.value,
                        })
                        .run();
                      setOrderedOpen(false);
                    }}
                  >
                    {style.label}
                  </ListStyleItem>
                );
              })}
            </ListStyleDropdown>
          )}
        </ListDropdownWrapper>

        {/* Horizontal Line */}
        <ToolbarButton
          title="Add Horizontal Line"
          icon={<RulerIcon size={20} />}
          onClick={() => editor.chain().focus().addWritingLine().run()}
        />
      </ToolbarGroup>
    </ToolbarContainer>
  );
}

interface ToolbarButtonProps {
  title: string;
  icon: React.ReactNode;
  isActive?: boolean;
  showTitle?: boolean;
  onClick: () => void;
}
function ToolbarButton({
  title,
  icon,
  isActive = false,
  showTitle = false,
  onClick,
}: ToolbarButtonProps) {
  return (
    <Button
      key={title}
      variant="ghost"
      size={showTitle ? 'default' : 'icon'}
      onClick={onClick}
      className={cn('gap-2', isActive ? 'bg-neutral-200/80' : '')}
      title={title}
    >
      {icon}
      {showTitle && <span>{title}</span>}
    </Button>
  );
}

interface ToolbarContainerProps {
  children: React.ReactNode[];
}
function ToolbarContainer({ children }: ToolbarContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const childRefs = useRef<Array<HTMLDivElement | null>>([]);
  const childWidths = useRef<number[]>([]);

  const [overflowPopoverOpen, setOverflowPopoverOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(children.length);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    childWidths.current = childRefs.current
      .slice(0, children.length)
      .map((el) => el?.getBoundingClientRect().width || 0);
  }, [children.length]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const fitCount = (reserve: number) => {
      const available =
        containerRef.current!.getBoundingClientRect().width - reserve;
      let sum = 0;
      for (let i = 0; i < childWidths.current.length; i++) {
        sum += childWidths.current[i];
        if (sum > available) return i;
      }
      return childWidths.current.length;
    };

    const ro = new ResizeObserver(() => {
      const firstPass = fitCount(0);
      const finalCount = firstPass < children.length ? fitCount(53) : firstPass;

      setVisibleCount((prev) => (prev !== finalCount ? finalCount : prev));
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [children.length]);

  const visible = children.slice(0, visibleCount);
  const overflow = children.slice(visibleCount);

  return (
    <Container ref={containerRef}>
      {visible.map((child, i) => (
        <div
          key={i}
          ref={(el) => (childRefs.current[i] = el)}
          style={{ display: 'inline-block' }}
        >
          {child}
        </div>
      ))}

      {overflow.length > 0 && (
        <ToolbarGroup>
          <Popover
            open={overflowPopoverOpen}
            onOpenChange={setOverflowPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon">
                <DotsThreeVerticalIcon size={20} />
              </Button>
            </PopoverTrigger>
            <StyledPopoverContent
              align="end"
              onInteractOutside={(event) => {
                event.preventDefault();
              }}
              // but when you click outside, close manually
              onPointerDownOutside={() => {
                setOverflowPopoverOpen(false);
              }}
            >
              {overflow.map((child, i) => (
                <div key={i}>{child}</div>
              ))}
            </StyledPopoverContent>
          </Popover>
        </ToolbarGroup>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 4px 8px;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const StyledPopoverContent = styled(PopoverContent)`
  display: flex;
  width: fit-content;
  max-width: 100vw;
  flex-wrap: wrap;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 4px 8px;
`;

const ToolbarGroup = tw.div`flex items-center h-full`;

const FontWrapper = styled.div`
  position: relative;
`;

const FontTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.1s ease;
  min-width: 140px;

  &:hover {
    background-color: #f0f0f0;
    border-color: #ccc;
  }
`;

const FontLabel = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FontDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
`;

const FontItem = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  background-color: ${(p) => (p.$active ? '#f0f0f0' : 'transparent')};
  color: ${(p) => (p.$active ? '#000' : '#333')};
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
    color: #000;
  }
`;

const FontSizeInput = styled(Input)`
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
`;

/* Heading dropdown styles */

const HeadingWrapper = styled.div`
  position: relative;
`;

const HeadingTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.1s ease;
  min-width: 120px;

  &:hover {
    background-color: #f0f0f0;
    border-color: #ccc;
  }
`;

const HeadingLabel = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeadingDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 160px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
`;

const HeadingItem = styled.button<{ $active?: boolean; $level: number }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  font-size: ${(p) =>
    p.$level === 1
      ? '18px'
      : p.$level === 2
        ? '15px'
        : p.$level === 3
          ? '13px'
          : '13px'};
  font-weight: ${(p) => (p.$level > 0 ? '600' : '400')};
  background-color: ${(p) => (p.$active ? '#f0f0f0' : 'transparent')};
  color: ${(p) => (p.$active ? '#000' : '#333')};
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
    color: #000;
  }
`;

/* Color dropdown shared styles */

const ColorDropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const ColorIconWrapper = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`;

const ColorBar = styled.span`
  display: block;
  width: 16px;
  height: 3px;
  border-radius: 1px;
`;

const ColorGridDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
`;

const ColorGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, 1fr);
  gap: 3px;
`;

const ColorSwatch = styled.button<{ $color: string; $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 3px;
  border: 1px solid ${(p) => (p.$active ? '#333' : '#e0e0e0')};
  background-color: ${(p) => p.$color};
  cursor: pointer;
  box-shadow: ${(p) => (p.$active ? 'inset 0 0 0 1px #fff' : 'none')};

  &:hover {
    border-color: #333;
    transform: scale(1.15);
  }
`;

const ResetButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 4px 0;
  border: none;
  border-top: 1px solid #e0e0e0;
  background: transparent;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  text-align: center;

  &:hover {
    color: #333;
    background-color: #f5f5f5;
  }
`;

/* List dropdown styles */

const ListDropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const CaretButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #666;
  margin-left: -4px;

  &:hover {
    color: #333;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
`;

const ListStyleDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 160px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
`;

const ListStyleItem = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  background-color: ${(p) => (p.$active ? '#f0f0f0' : 'transparent')};
  color: ${(p) => (p.$active ? '#000' : '#333')};
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
    color: #000;
  }
`;
